import React, { useMemo } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';

const { Text } = Typography;

const AdjustmentsFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { options: empOptions } = useEmployeeOptions();

  const year = Number(search.get('year') || dayjs().year());
  const month = Number(search.get('month') || dayjs().month() + 1);

  const adjustmentType = Form.useWatch('adjustment_type', form);
  const salaryComponentId = Form.useWatch('salary_component_id', form);

  const { data: salaryHeads = [], isLoading: headsLoading } = useQuery({
    queryKey: hrmsKeys.salaryHeads(),
    queryFn: () => hrmsService.listSalaryHeads(),
    staleTime: 60_000,
  });

  const headOptions = useMemo(() => {
    const wantDeduction = adjustmentType === 'Deduction';
    return (salaryHeads || [])
      .filter((h: any) => h.is_active !== false)
      .filter((h: any) => {
        const isDed = String(h.component_type || '').toLowerCase() === 'deduction';
        return wantDeduction ? isDed : !isDed;
      })
      .map((h: any) => ({
        value: h.id,
        label: `${h.component_code} — ${h.component_name}`,
        head: h,
      }));
  }, [salaryHeads, adjustmentType]);

  const selectedHead = useMemo(
    () => (salaryHeads || []).find((h: any) => h.id === salaryComponentId),
    [salaryHeads, salaryComponentId]
  );

  const onHeadChange = (id: string) => {
    const head = (salaryHeads || []).find((h: any) => h.id === id);
    if (!head) return;
    const isDed = String(head.component_type || '').toLowerCase() === 'deduction';
    form.setFieldsValue({
      salary_component_id: head.id,
      component_code: head.component_code,
      component_name: head.component_name,
      adjustment_type: isDed ? 'Deduction' : 'Addition',
    });
  };

  const onTypeChange = (type: string) => {
    const head = selectedHead;
    if (!head) {
      form.setFieldsValue({ salary_component_id: undefined, component_code: undefined, component_name: undefined });
      return;
    }
    const isDed = String(head.component_type || '').toLowerCase() === 'deduction';
    const matches = type === 'Deduction' ? isDed : !isDed;
    if (!matches) {
      form.setFieldsValue({
        salary_component_id: undefined,
        component_code: undefined,
        component_name: undefined,
      });
    }
  };

  const save = async () => {
    try {
      const v = await form.validateFields();
      await hrmsService.createAdjustment({
        employee_id: v.employee_id,
        salary_component_id: v.salary_component_id,
        component_code: v.component_code,
        component_name: v.component_name,
        adjustment_type: v.adjustment_type,
        amount: v.amount,
        is_recurring: v.is_recurring,
        notes: v.notes,
        period_year: year,
        period_month: month,
      });
      message.success('Adjustment booked');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.adjustments() });
      navigate('/hr/adjustments');
    } catch (e: unknown) {
      if ((e as any)?.errorFields) return;
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Create failed');
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Book adjustment"
          subtitle={`Period ${String(month).padStart(2, '0')}/${year}`}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hr/adjustments')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Linked to salary head for accounting"
        description={
          <span>
            Choose a <Link to="/hr/salary-heads">Salary head</Link>. Component code/name and GL expense account come
            from that master so payroll compute can map additions to the correct expense account.
          </span>
        }
      />
      <Form form={form} layout="vertical" initialValues={{ adjustment_type: 'Addition', is_recurring: false }}>
        <Card title="Addition / Deduction">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select options={empOptions} showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="adjustment_type" label="Type" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'Addition', label: 'Addition (earning head)' },
                    { value: 'Deduction', label: 'Deduction (deduction head)' },
                  ]}
                  onChange={onTypeChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="salary_component_id"
                label="Salary head"
                rules={[{ required: true, message: 'Select a salary head' }]}
                extra={
                  selectedHead ? (
                    <Text type="secondary">
                      Code <Text code>{selectedHead.component_code}</Text> · {selectedHead.component_name}
                      {selectedHead.gl_expense_account_id
                        ? ' · GL expense account mapped'
                        : ' · No GL expense account on this head yet'}
                    </Text>
                  ) : (
                    <Text type="secondary">Filtered by type (Earning vs Deduction heads)</Text>
                  )
                }
              >
                <Select
                  loading={headsLoading}
                  options={headOptions}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select salary head"
                  onChange={onHeadChange}
                  notFoundContent={
                    headsLoading ? 'Loading…' : 'No matching salary heads — create them under Salary heads'
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="component_code" label="Component code" rules={[{ required: true }]}>
                <Input disabled placeholder="From salary head" />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="component_name" label="Component name" rules={[{ required: true }]}>
                <Input disabled placeholder="From salary head" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0.01} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="is_recurring" label="Recurring" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default AdjustmentsFormPage;
